/**
 * The polymer families of the catalogue — ONE list for costing and the catalogue.
 *
 * Mirror of `shared/py/bolloprint_shared/material_polymers.py`; the backend test
 * `test_material_polymers.py` fails if the two drift apart. A polymer is priced
 * by exact name, so a family missing from here would cost 0 €/kg in silence.
 *
 * The polymer names are NEVER translated ("PLA" is "PLA" in every language).
 * The group KEYS are stable ids; each app labels them itself (the tenant with
 * `t('materialGroup.<key>')`, the Spanish-only admin with its own map).
 */

export type FilamentPolymerGroupKey = 'common' | 'polyamides' | 'highPerformance' | 'other';

export interface PolymerGroup<K extends string = string> {
    key: K;
    polymers: readonly string[];
}

/** A list of groups is never empty — typed so `[0]` needs no guard. */
export type PolymerGroups<K extends string = string> = readonly [PolymerGroup<K>, ...PolymerGroup<K>[]];

/** Filament families by group, in the order the screens show them. */
export const FILAMENT_POLYMER_GROUPS: PolymerGroups<FilamentPolymerGroupKey> = [
    { key: 'common', polymers: ['PLA', 'PETG', 'ABS', 'ASA', 'TPU', 'PC', 'PP', 'PVA'] },
    { key: 'polyamides', polymers: ['PA6', 'PA12', 'PA11', 'PA'] },
    { key: 'highPerformance', polymers: ['PPA', 'PPS', 'PVDF', 'PEI', 'PEEK'] },
    {
        key: 'other',
        polymers: [
            'PCTG', 'PET', 'CPE', 'HIPS', 'PVB', 'TPE',
            'PEBA', 'PVC', 'PCL', 'BVOH', 'SUPPORT', 'CLEANING',
        ],
    },
];

export const FILAMENT_POLYMER_LIST: readonly string[] = FILAMENT_POLYMER_GROUPS.flatMap((g) => g.polymers);

export const RESIN_POLYMER_LIST: readonly string[] = [
    'Standard', 'ABS-Like', 'Water-Washable', 'Tough', 'Flexible',
    'Castable', 'High-Temp', 'Rigid', 'Dental',
];

/**
 * The groups a family's rate card shows. A card splits into groups only when it
 * has more than 12 families (owner decision, 2026-09-23): filament (29) goes by
 * group, resin (9) shows whole as one group with key `all`. If resin ever passes
 * 12, it gets groups here and its card picks them up.
 */
export function polymerGroupsFor(family: 'filament' | 'resin'): PolymerGroups {
    return family === 'filament' ? FILAMENT_POLYMER_GROUPS : [{ key: 'all', polymers: RESIN_POLYMER_LIST }];
}
